import React, { useMemo } from "react";

import { faLock, faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Control,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { Checkbox } from "src/base-components/Checkbox";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import { FlowFolderListItem } from "src/clients/flow-api";
import { Pill } from "src/design-system/Pill";
import {
  GranularRole,
  GranularRoleKey,
  getCrossVerticalGranularRoles,
  getDependentGranularRoles,
  getImpliedGranularRoles,
  getRequiredGranularRoles,
  getWorkspaceRoleGranularRoles,
  LOGIC_ALL_FOLDERS_VERTICAL,
  VerticalDependencyKey,
} from "src/userManagement/userPermissions/permissionFeatures";
import {
  GranularRolesFieldName,
  GranularRolesRow,
  getGranularRolesRows,
  granularRolesFieldName,
  UserPermissionsFormValues,
} from "src/userManagement/userPermissions/utils";

const EMPTY_SELECTION: GranularRoleKey[] = [];

// Selected granular roles that can't be removed because a selected granular role still depends on them.
const getLockedGranularRoles = (
  dependencyKey: VerticalDependencyKey,
  selectedGranularRoles: GranularRoleKey[],
): GranularRoleKey[] =>
  selectedGranularRoles.filter((granularRoleKey) =>
    getDependentGranularRoles(dependencyKey, granularRoleKey).some(
      (dependent) => selectedGranularRoles.includes(dependent),
    ),
  );

const chipIcon = (selected: boolean, locked: boolean) => {
  if (locked) {
    return faLock;
  }
  return selected ? faXmark : faPlus;
};

const GranularRoleChip: React.FC<{
  dataLoc: string;
  label: string;
  selected: boolean;
  locked: boolean;
  onToggle: () => void;
}> = ({ dataLoc, label, selected, locked, onToggle }) => (
  <Pill
    data-locked={String(locked)}
    data-selected={String(selected)}
    dataLoc={dataLoc}
    fontType="code-12"
    size="sm"
    variant={selected ? "indigo-light" : "gray"}
    onClick={onToggle}
  >
    <Pill.Text>{label}</Pill.Text>
    <Pill.Icon icon={chipIcon(selected, locked)} />
  </Pill>
);

const useGranularRolesField = (rowId: string) => {
  const { field } = useController<
    UserPermissionsFormValues,
    GranularRolesFieldName
  >({
    name: granularRolesFieldName(rowId),
    defaultValue: EMPTY_SELECTION,
  });

  return {
    ownGranularRoles: field.value ?? EMPTY_SELECTION,
    setGranularRoles: field.onChange,
  };
};

const useWatchGranularRoles = (
  control: Control<UserPermissionsFormValues>,
  rowIds: string[],
) =>
  useWatch<UserPermissionsFormValues, GranularRolesFieldName[]>({
    control,
    name: rowIds.map(granularRolesFieldName),
  });

const onRow = (
  row: GranularRolesRow,
  granularRoles: GranularRoleKey[],
): GranularRoleKey[] =>
  granularRoles.filter((granularRole) =>
    row.granularRoles.some((option) => option.key === granularRole),
  );

/**
 * Roles the user already has without being granted them on the row showing
 * them: the ones the workspace role carries, and the ones a workspace-wide
 * selection carries into another vertical. Only an all-folders grant counts as
 * workspace-wide — a single folder's view carries sandbox history for that
 * folder alone, which the Decision History row has no way to express.
 */
const carriedGranularRoles = (
  fromAllFolders: GranularRoleKey[],
  workspaceRole: string,
): GranularRoleKey[] => {
  const fromWorkspaceRole = getWorkspaceRoleGranularRoles(workspaceRole);

  return [
    ...fromWorkspaceRole,
    ...getCrossVerticalGranularRoles([...fromAllFolders, ...fromWorkspaceRole]),
  ];
};

const useCarriedGranularRoles = (row: GranularRolesRow): GranularRoleKey[] => {
  const { control } = useFormContext<UserPermissionsFormValues>();
  const workspaceRole = useWatch<UserPermissionsFormValues, "workspaceRole">({
    control,
    name: "workspaceRole",
  });
  const [fromAllFolders = EMPTY_SELECTION] = useWatchGranularRoles(control, [
    LOGIC_ALL_FOLDERS_VERTICAL.key,
  ]);

  return onRow(row, carriedGranularRoles(fromAllFolders, workspaceRole));
};

const useGranularRolesCell = (
  row: GranularRolesRow,
  inheritedGranularRoles: GranularRoleKey[],
) => {
  const { ownGranularRoles, setGranularRoles } = useGranularRolesField(row.id);

  // A selected role contains others, which read as selected on their own
  // footing: dropping whatever else carries them must not unselect them.
  const selectedGranularRoles = [
    ...new Set([
      ...ownGranularRoles,
      ...ownGranularRoles.flatMap(getImpliedGranularRoles),
      ...inheritedGranularRoles,
    ]),
  ];
  const lockedGranularRoles = [
    ...new Set([
      ...inheritedGranularRoles,
      ...getLockedGranularRoles(row.dependencyKey, selectedGranularRoles),
    ]),
  ];

  const handleToggleGranularRole = (granularRoleKey: GranularRoleKey) => {
    if (lockedGranularRoles.includes(granularRoleKey)) {
      return;
    }

    if (selectedGranularRoles.includes(granularRoleKey)) {
      setGranularRoles(
        ownGranularRoles.filter((key) => key !== granularRoleKey),
      );
      return;
    }

    setGranularRoles([
      ...new Set([
        ...ownGranularRoles,
        granularRoleKey,
        ...getRequiredGranularRoles(row.dependencyKey, granularRoleKey).filter(
          (key) => !selectedGranularRoles.includes(key),
        ),
      ]),
    ]);
  };

  return {
    selectedGranularRoles,
    lockedGranularRoles,
    handleToggleGranularRole,
  };
};

const GranularRoleChips: React.FC<{
  rowId: string;
  granularRoles: GranularRole[];
  selectedGranularRoles: GranularRoleKey[];
  lockedGranularRoles: GranularRoleKey[];
  onToggleGranularRole: (granularRoleKey: GranularRoleKey) => void;
}> = ({
  rowId,
  granularRoles,
  selectedGranularRoles,
  lockedGranularRoles,
  onToggleGranularRole,
}) => (
  <div className="flex flex-wrap gap-2">
    {granularRoles.map((granularRole) => (
      <GranularRoleChip
        key={granularRole.key}
        dataLoc={`granular-role-${rowId}-${granularRole.key}`}
        label={granularRole.label}
        locked={lockedGranularRoles.includes(granularRole.key)}
        selected={selectedGranularRoles.includes(granularRole.key)}
        onToggle={() => onToggleGranularRole(granularRole.key)}
      />
    ))}
  </div>
);

const VerticalPermissionsCell: React.FC<{ row: GranularRolesRow }> = ({
  row,
}) => {
  const cell = useGranularRolesCell(row, useCarriedGranularRoles(row));

  return (
    <GranularRoleChips
      granularRoles={row.granularRoles}
      lockedGranularRoles={cell.lockedGranularRoles}
      rowId={row.id}
      selectedGranularRoles={cell.selectedGranularRoles}
      onToggleGranularRole={cell.handleToggleGranularRole}
    />
  );
};

const FolderPermissionsCell: React.FC<{ row: GranularRolesRow }> = ({
  row,
}) => {
  const { control } = useFormContext<UserPermissionsFormValues>();
  const [fromAllFolders = EMPTY_SELECTION] = useWatchGranularRoles(control, [
    LOGIC_ALL_FOLDERS_VERTICAL.key,
  ]);
  const carried = useCarriedGranularRoles(row);
  const cell = useGranularRolesCell(row, [
    ...new Set([...fromAllFolders, ...carried]),
  ]);

  return (
    <GranularRoleChips
      granularRoles={row.granularRoles}
      lockedGranularRoles={cell.lockedGranularRoles}
      rowId={row.id}
      selectedGranularRoles={cell.selectedGranularRoles}
      onToggleGranularRole={cell.handleToggleGranularRole}
    />
  );
};

/** What this row can actually grant: its roles minus the ones already carried. */
const grantableGranularRoles = (
  row: GranularRolesRow,
  carried: GranularRoleKey[],
): GranularRoleKey[] =>
  row.granularRoles
    .map((granularRole) => granularRole.key)
    .filter((granularRole) => !carried.includes(granularRole));

const GranularRolesSelectCell: React.FC<{ row: GranularRolesRow }> = ({
  row,
}) => {
  const { ownGranularRoles, setGranularRoles } = useGranularRolesField(row.id);
  const grantable = grantableGranularRoles(row, useCarriedGranularRoles(row));
  const allSelected = grantable.every((granularRole) =>
    ownGranularRoles.includes(granularRole),
  );

  return (
    <Checkbox
      checked={allSelected}
      dataLoc={`granular-roles-select-${row.id}`}
      indeterminate={!allSelected && ownGranularRoles.length > 0}
      onChange={() => setGranularRoles(allSelected ? [] : grantable)}
    />
  );
};

const GranularRolesSelectAllCell: React.FC<{ rows: GranularRolesRow[] }> = ({
  rows,
}) => {
  const { control, setValue } = useFormContext<UserPermissionsFormValues>();
  const granularRolesByRow = useWatchGranularRoles(
    control,
    rows.map((row) => row.id),
  );
  const workspaceRole = useWatch<UserPermissionsFormValues, "workspaceRole">({
    control,
    name: "workspaceRole",
  });

  const selectedByRow = rows.map(
    (_row, index) => granularRolesByRow[index] ?? EMPTY_SELECTION,
  );
  const allFoldersIndex = rows.findIndex(
    (row) => row.id === LOGIC_ALL_FOLDERS_VERTICAL.key,
  );
  const carried = carriedGranularRoles(
    selectedByRow[allFoldersIndex] ?? EMPTY_SELECTION,
    workspaceRole,
  );
  const grantableByRow = rows.map((row) =>
    grantableGranularRoles(row, onRow(row, carried)),
  );
  const allSelected = rows.every((_row, index) =>
    grantableByRow[index].every((granularRole) =>
      selectedByRow[index].includes(granularRole),
    ),
  );
  const someSelected = selectedByRow.some((selected) => selected.length > 0);

  return (
    <Checkbox
      checked={allSelected}
      dataLoc="granular-roles-select-all"
      indeterminate={!allSelected && someSelected}
      onChange={() =>
        rows.map((row, index) =>
          setValue(
            granularRolesFieldName(row.id),
            allSelected ? [] : grantableByRow[index],
          ),
        )
      }
    />
  );
};

const columnHelper = createColumnHelper<GranularRolesRow>();

const ROW_HEIGHT = "py-2 h-auto";

export const Columns = [
  columnHelper.display({
    id: "select",
    header: (info) => {
      const verticalRows = info.table
        .getRowModel()
        .rows.filter((row) => !row.original.isFolder)
        .map((row) => row.original);

      return (
        <Header info={info}>
          {verticalRows.length > 0 ? (
            <GranularRolesSelectAllCell rows={verticalRows} />
          ) : (
            <Checkbox checked={false} readOnly />
          )}
        </Header>
      );
    },
    cell: (info) => (
      <Cell
        className={twJoin("items-center bg-transparent", ROW_HEIGHT)}
        info={info}
      >
        {!info.row.original.isFolder && (
          <GranularRolesSelectCell row={info.row.original} />
        )}
      </Cell>
    ),
    minSize: 40,
    maxSize: 40,
    meta: { sticky: "left" },
  }),
  columnHelper.accessor("label", {
    header: (info) => <Header info={info}>Feature</Header>,
    cell: (info) => (
      <Cell
        className={ROW_HEIGHT}
        dataLoc={`granular-roles-vertical-${info.row.original.id}`}
        info={info}
      >
        <span className="block truncate">{info.getValue()}</span>
      </Cell>
    ),
    minSize: 232,
    maxSize: 232,
  }),
  columnHelper.display({
    id: "permissions",
    header: (info) => <Header info={info}>Permissions</Header>,
    cell: (info) => (
      <Cell className={ROW_HEIGHT} info={info}>
        {info.row.original.isFolder ? (
          <FolderPermissionsCell row={info.row.original} />
        ) : (
          <VerticalPermissionsCell row={info.row.original} />
        )}
      </Cell>
    ),
  }),
  columnHelper.accessor("keysCount", {
    header: (info) => <Header info={info}>Keys</Header>,
    cell: (info) => (
      <Cell
        className={twJoin(ROW_HEIGHT)}
        dataLoc={`granular-roles-keys-${info.row.original.id}`}
        info={info}
      >
        {info.getValue()}
      </Cell>
    ),
    minSize: 122,
    maxSize: 122,
  }),
];

type Props = {
  folders: FlowFolderListItem[];
};
export const UserGranularRolesTable: React.FC<Props> = ({ folders }) => {
  const rows = useMemo(() => getGranularRolesRows(folders), [folders]);

  return (
    <div className="pt-4">
      <Table
        columns={Columns}
        data={rows}
        dataLoc="user-granular-roles-table"
        getRowId={(row) => row.id}
        rowClassName="hover:bg-gray-50"
        disableVirtualization
      />
    </div>
  );
};
